import { useCallback, useEffect, useState } from "react";
import { useAuiState } from "@assistant-ui/react";
import { useTranslation } from "react-i18next";
import { findUnstableEntry } from "@/lib/assistantUiTypes";
import { apiAuthFetch } from "@/lib/authFetch";
import { appendLocParam } from "@/lib/i18n";
import { appEvents } from "@/lib/events";
import { ClientLogger } from "@/lib/clientLogger";

/**
 * All RetryCard state logic — extracted for testability.
 * Determines visibility, button label, countdown, and click handler.
 */
export function useRetryCardState() {
  const { t, i18n } = useTranslation();
  const backendMessageId = useAuiState(({ message }) =>
    findUnstableEntry(message.metadata?.unstable_data, "backend-message-id")?.id
  );
  const localMessageId = useAuiState(({ message }) => message.id);
  const messageId = backendMessageId ?? localMessageId;
  const remoteId = useAuiState(state => state.threadListItem.remoteId);
  const lang = i18n.language ?? "cs";

  const terminalState = useAuiState(({ message }) => {
    return findUnstableEntry(message.metadata?.unstable_data, "terminal-state")?.state;
  });

  const retryInfo = useAuiState(({ message }) => {
    return findUnstableEntry(message.metadata?.unstable_data, "retry-info");
  });

  // Determine if message has content (i.e. partial response exists → "Continue")
  const hasContent = useAuiState(({ message }) => {
    if (!message.content) return false;
    return message.content.some(
      (p: { type: string; text?: string }) => p.type === "text" && (p.text?.length ?? 0) > 0,
    );
  });

  const isLatestAssistant = useAuiState(state => {
    const messages = state.thread.messages;
    if (!messages?.length) return false;
    const last = messages[messages.length - 1];
    return last.role === "assistant" && last.id === state.message.id;
  });

  // Countdown timer state
  const [secondsLeft, setSecondsLeft] = useState<number>(retryInfo?.retryAfterSec ?? 0);
  const [hidden, setHidden] = useState(false);
  const [pending, setPending] = useState(false);

  // Reset countdown when retryInfo changes
  useEffect(() => {
    setSecondsLeft(retryInfo?.retryAfterSec ?? 0);
  }, [retryInfo?.retryAfterSec]);

  // Tick the countdown every second
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = terminalState === "Interrupted" && isLatestAssistant && !hidden;
  const actionLabel = hasContent ? t("retry.continueButton") : t("retry.retryButton");
  const countdownLabel = hasContent
    ? t("retry.countdownContinue", { seconds: secondsLeft })
    : t("retry.countdownRetry", { seconds: secondsLeft });
  const serverMessage = retryInfo?.message ?? null;

  const onAction = useCallback(async () => {
    if (!remoteId || !messageId || pending) return;
    setHidden(true);
    setPending(true);

    try {
      const res = await apiAuthFetch(
        appendLocParam(`api/threads/${remoteId}/messages/${messageId}/continue`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lang }),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        ClientLogger.warn("Retry endpoint rejected", {
          component: "useRetryCardState", metadata: { status: res.status, body },
        });
        setHidden(false);
        return;
      }

      appEvents.emit("continue-started", { threadId: remoteId, messageId });
    } catch (err) {
      ClientLogger.error("Retry card network error", {
        error: err instanceof Error ? err : undefined, component: "useRetryCardState",
      });
      setHidden(false);
    }finally {
      setPending(false);
    }
  }, [remoteId, messageId, lang, pending]);

  return {
    visible,
    pending,
    secondsLeft,
    actionLabel,
    countdownLabel,
    serverMessage,
    onAction,
  };
}
